import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import { Close, Fullscreen } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

interface ExpandableSidePanelProps {
  title: string;
  subtitle?: string;
  count?: number;
  children: React.ReactNode;
  expandable?: boolean;
  onExpand?: () => void;
  maxContentHeight?: number | string;
  fill?: boolean;
}

export const ExpandableSidePanel = ({
  title,
  subtitle,
  count,
  children,
  expandable,
  onExpand,
  maxContentHeight,
  fill,
}: ExpandableSidePanelProps) => {
  const { theme } = useCampfireTheme();
  const [expanded, setExpanded] = useState(false);
  const canExpandInline = Boolean(expandable) && !onExpand;

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
      return;
    }
    setExpanded(true);
  };

  return (
    <Box
      width='100%'
      style={{
        backgroundColor: '#ffffff',
        border: `1px solid ${theme.color.grey.border}`,
        borderRadius: 10,
        overflow: 'hidden',
        ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        style={{
          gap: 8,
          padding: '14px 18px',
          borderBottom: `1px solid ${theme.color.grey.border}`,
          flexShrink: 0,
        }}
      >
        <Typography style={{ fontWeight: 700, fontSize: '0.875rem', color: theme.color.grey.neutralBrand800 }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Box
            style={{
              backgroundColor: '#F2F2F2',
              color: theme.color.grey.neutralBrand400,
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: '0.6875rem',
              fontWeight: 600,
            }}
          >
            {count}
          </Box>
        )}
        <Box flexGrow={1} />
        {(canExpandInline || onExpand) && (
          <IconButton
            size='small'
            onClick={handleExpand}
            title='Expand View'
            style={{
              padding: 4,
              border: `1px solid ${theme.color.grey.neutral200}`,
              borderRadius: 4,
              backgroundColor: '#ffffff',
            }}
          >
            <Fullscreen style={{ fontSize: '20px', color: theme.color.grey.neutral300 }} />
          </IconButton>
        )}
      </Box>

      <Box
        style={{
          padding: '14px 18px',
          maxHeight: fill ? undefined : maxContentHeight,
          overflowY: fill || maxContentHeight ? 'auto' : undefined,
          ...(fill ? { flexGrow: 1, minHeight: 0 } : {}),
        }}
      >
        {!expanded && children}
      </Box>

      {canExpandInline && (
        <Dialog
          open={expanded}
          onClose={() => setExpanded(false)}
          maxWidth={false}
          PaperProps={{
            style: {
              width: '90vw',
              height: '90vh',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DialogTitle style={{ flexShrink: 0 }}>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
              <Box>
                <Box style={{ fontSize: '20px', fontWeight: 500 }}>{title}</Box>
                {subtitle && <Box style={{ fontSize: '14px', color: theme.color.grey.neutral300 }}>{subtitle}</Box>}
              </Box>
              <IconButton onClick={() => setExpanded(false)} size='small'>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent style={{ flex: 1, overflow: 'auto', padding: '16px' }}>{expanded && children}</DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
